import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Trash2, Bell, User, Shield, CreditCard, Mail, Send, Check, BookOpen } from "lucide-react";

const reminderOptions = [
  { v: "daily", l: "A question every day" },
  { v: "twice_weekly", l: "Twice a week" },
  { v: "weekly", l: "Once a week" },
  { v: "none", l: "No reminders" },
];

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reminder, setReminder] = useState(user?.reminder_prefs?.frequency || "weekly");
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscribing, setSubscribing] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [bookEmail, setBookEmail] = useState(null);
  const [bookSending, setBookSending] = useState(false);
  const [banner, setBanner] = useState({ manual: false, text: "", start_date: "", end_date: "" });

  useEffect(() => { api.get("/profile").then(({ data }) => setProfile(data)); }, []);
  useEffect(() => { api.get("/payments/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.is_admin) api.get("/settings/gift-banner").then(({ data }) => setBanner(data)).catch(() => {}); }, [user]);

  const saveBanner = async () => {
    try { await api.put("/admin/gift-banner", { enabled: banner.manual, text: banner.text, start_date: banner.start_date || null, end_date: banner.end_date || null }); toast.success("Gift banner updated"); }
    catch { toast.error("Couldn't save the banner."); }
  };

  const subscribe = async (lookup_key) => {
    setSubscribing(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Couldn't start checkout. Please try again."); setSubscribing(""); }
  };

  const previewEmail = async () => {
    const { data } = await api.get("/reminders/preview");
    setEmailPreview(data);
  };
  const manageBilling = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open the billing portal.");
    }
  };
  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/reminders/send-test");
      toast[data.sent ? "success" : "message"](data.message);
    } finally { setSendingTest(false); }
  };
  const previewBookProgress = async () => {
    const { data } = await api.get("/book-progress/preview");
    setBookEmail(data);
  };
  const sendBookProgress = async () => {
    setBookSending(true);
    try {
      const { data } = await api.post("/book-progress/send-test");
      toast[data.ok ? "success" : "message"](data.message);
    } finally { setBookSending(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        preferred_name: profile.preferred_name,
        birth_year: profile.birth_year ? parseInt(profile.birth_year, 10) : null,
        birth_place: profile.birth_place, grew_up_place: profile.grew_up_place, memoir_for: profile.memoir_for,
      });
      await refreshUser(); toast.success("Profile saved");
    } finally { setSaving(false); }
  };

  const saveReminder = async (v) => { setReminder(v); await api.put("/reminders", { frequency: v }); toast.success("Reminder preference saved"); };

  const exportData = async () => {
    const { data } = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "write-my-story-export.json"; a.click();
    URL.revokeObjectURL(url); toast.success("Your data has been downloaded");
  };

  const deleteAccount = async () => {
    if (!window.confirm("This permanently deletes your account and every memory. Are you absolutely sure?")) return;
    await api.delete("/account"); await logout();
  };

  if (!profile) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;
  const sub = user?.subscription;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <h1 className="font-serif text-3xl text-charcoal">Settings</h1>

      {user?.is_admin && (
        <div data-testid="admin-gift-banner" className="bg-white rounded-2xl border border-line p-6">
          <div className="flex items-center gap-2 text-sage font-semibold text-sm"><Shield size={16} /> Admin · Gift banner</div>
          <p className="text-subtle text-sm mt-1">Show a seasonal banner across the site (e.g. "Order by 20 Dec for Christmas"). It links to the gift page.</p>
          <label className="mt-4 flex items-center gap-2 text-sm text-charcoal cursor-pointer">
            <input data-testid="admin-banner-enabled" type="checkbox" checked={!!banner.manual} onChange={(e) => setBanner((b) => ({ ...b, manual: e.target.checked }))} className="w-4 h-4 accent-teal" />
            Show the banner now
          </label>
          <input data-testid="admin-banner-text" value={banner.text || ""} onChange={(e) => setBanner((b) => ({ ...b, text: e.target.value }))} placeholder="e.g. Order by 20 Dec for Christmas delivery" className="mt-3 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          <p className="mt-4 text-sm text-charcoal font-medium">Or schedule it automatically</p>
          <p className="text-xs text-subtle">Set both dates and the banner shows only within that window (this overrides the toggle above). Leave blank to control it manually.</p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div><label className="text-xs text-subtle">From</label><input data-testid="admin-banner-start" type="date" value={banner.start_date || ""} onChange={(e) => setBanner((b) => ({ ...b, start_date: e.target.value }))} className="mt-1 w-full h-11 px-3 rounded-xl border border-line outline-none focus:border-teal" /></div>
            <div><label className="text-xs text-subtle">Until</label><input data-testid="admin-banner-end" type="date" value={banner.end_date || ""} onChange={(e) => setBanner((b) => ({ ...b, end_date: e.target.value }))} className="mt-1 w-full h-11 px-3 rounded-xl border border-line outline-none focus:border-teal" /></div>
          </div>
          <button data-testid="admin-banner-save" onClick={saveBanner} className="mt-3 inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm">Save banner</button>
          {(banner.start_date || banner.end_date) && (
            <button data-testid="admin-banner-clear" onClick={() => setBanner((b) => ({ ...b, start_date: "", end_date: "" }))} className="mt-3 ml-2 inline-flex items-center gap-2 text-subtle hover:text-charcoal px-4 py-2.5 text-sm">Clear schedule</button>
          )}
        </div>
      )}

      {/* Subscription */}
      <div className="bg-teal rounded-2xl p-6 text-cream">
        <p className="text-xs uppercase tracking-widest text-cream/60 font-semibold">Your plan</p>
        <p className="font-serif text-2xl mt-1 capitalize">{sub?.plan === "trial" ? "Free trial" : sub?.plan === "annual" ? "Yearly" : sub?.plan}</p>
        {(sub?.status === "trial" || sub?.status === "trialing") && sub?.trial_ends && (
          <p className="text-cream/80 text-sm mt-1" data-testid="trial-ends">Free trial ends {new Date(sub.trial_ends).toLocaleDateString()}</p>
        )}
        <p className="text-cream/70 text-sm mt-3">Unlimited memories, voice recording, helpful questions, story polishing, timeline, chapters and book building.</p>
      </div>

      {/* Plans */}
      <div className="bg-white rounded-2xl border border-line p-6">
        <h2 className="font-serif text-xl text-charcoal flex items-center gap-2 mb-1"><CreditCard size={18} className="text-sage" /> Choose your plan</h2>
        <p className="text-subtle text-sm mb-4">7-day free trial on subscriptions. Prices are shown in GBP — you'll be charged in your local currency (e.g. USD, CAD) at checkout.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {plans.map((p) => {
            const planMap = { wms_monthly: "monthly", wms_yearly: "annual", wms_lifetime: "lifetime" };
            const isCurrent = sub?.plan === planMap[p.lookup_key];
            return (
            <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`} className={`border rounded-2xl p-5 flex flex-col transition-colors ${isCurrent ? "border-teal bg-sagelight" : "border-line hover:border-sage"}`}>
              <div className="flex items-center justify-between">
                <p className="font-serif text-lg text-charcoal">{p.name}</p>
                {isCurrent && <span className="text-xs font-semibold text-teal bg-white px-2 py-0.5 rounded-full flex items-center gap-1"><Check size={11} /> Current</span>}
              </div>
              <p className="mt-1"><span className="font-serif text-2xl text-teal">{p.price}</span> <span className="text-subtle text-sm">{p.cadence}</span></p>
              <p className="text-subtle text-sm mt-2 flex-1">{p.blurb}</p>
              {p.trial && !isCurrent && <p className="text-xs text-sage font-semibold mt-2">7-day free trial</p>}
              <button data-testid={`subscribe-${p.lookup_key}`} onClick={() => subscribe(p.lookup_key)} disabled={!!subscribing || isCurrent}
                className="mt-4 inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-50">
                {subscribing === p.lookup_key ? <Loader2 className="animate-spin" size={15} /> : null}
                {isCurrent ? "Your plan" : p.recurring ? "Start trial" : "Buy lifetime"}
              </button>
            </div>
          );})}
        </div>
        {["monthly", "annual", "lifetime"].includes(sub?.plan) && (
          <button data-testid="manage-billing-btn" onClick={manageBilling}
            className="mt-4 inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm">
            <CreditCard size={15} /> Manage or cancel my plan
          </button>
        )}
        <p className="text-xs text-subtle mt-3">Test mode — use card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>

      {/* Profile */}
      <div className="bg-white rounded-2xl border border-line p-6 space-y-4">
        <h2 className="font-serif text-xl text-charcoal flex items-center gap-2"><User size={18} className="text-sage" /> About you</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-charcoal">Name</label><input data-testid="profile-name-input" value={profile.preferred_name || ""} onChange={(e) => setProfile({ ...profile, preferred_name: e.target.value })} className="mt-1 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" /></div>
          <div><label className="text-sm text-charcoal">Birth year</label><input data-testid="profile-year-input" type="number" value={profile.birth_year || ""} onChange={(e) => setProfile({ ...profile, birth_year: e.target.value })} className="mt-1 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" /></div>
          <div><label className="text-sm text-charcoal">Born in</label><input value={profile.birth_place || ""} onChange={(e) => setProfile({ ...profile, birth_place: e.target.value })} className="mt-1 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" /></div>
          <div><label className="text-sm text-charcoal">Grew up in</label><input value={profile.grew_up_place || ""} onChange={(e) => setProfile({ ...profile, grew_up_place: e.target.value })} className="mt-1 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" /></div>
        </div>
        <button data-testid="profile-save-btn" onClick={saveProfile} disabled={saving} className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold">
          {saving ? <Loader2 className="animate-spin" size={16} /> : null} Save
        </button>
      </div>

      {/* Reminders */}
      <div className="bg-white rounded-2xl border border-line p-6">
        <h2 className="font-serif text-xl text-charcoal flex items-center gap-2 mb-4"><Bell size={18} className="text-sage" /> Reminders</h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {reminderOptions.map((o) => (
            <button key={o.v} data-testid={`reminder-${o.v}`} onClick={() => saveReminder(o.v)}
              className={`text-left px-4 py-3 rounded-xl border transition-colors ${reminder === o.v ? "border-teal bg-sagelight text-teal font-semibold" : "border-line hover:border-sage"}`}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-line flex flex-wrap gap-3 items-center">
          <button data-testid="preview-email-btn" onClick={previewEmail} className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm"><Mail size={15} /> Preview this week's email</button>
          <button data-testid="send-test-email-btn" onClick={sendTest} disabled={sendingTest} className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm">{sendingTest ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send me a test</button>
        </div>
        {emailPreview && (
          <div className="mt-4 bg-cream2 rounded-xl p-4" data-testid="email-preview">
            <p className="text-xs text-subtle">To: {emailPreview.to} · Subject: {emailPreview.subject}</p>
            <p className="font-serif text-lg text-teal mt-2">{emailPreview.question}</p>
            <p className="text-xs mt-2">{emailPreview.live_sending ? <span className="text-sage font-semibold">Live sending is on — emails deliver once your sending domain is verified in Resend.</span> : <span className="text-coral font-semibold">Preview only — add your Resend API key to send real emails.</span>}</p>
          </div>
        )}
      </div>

      {/* Your book so far — monthly progress email */}
      <div className="bg-white rounded-2xl border border-line p-6" data-testid="book-progress-section">
        <h2 className="font-serif text-xl text-charcoal flex items-center gap-2 mb-1"><BookOpen size={18} className="text-sage" /> Your book so far</h2>
        <p className="text-subtle text-sm">A warm monthly summary showing how your memoir is growing. Preview exactly what customers receive, or send yourself a copy.</p>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <button data-testid="preview-book-progress-btn" onClick={previewBookProgress} className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm"><Mail size={15} /> Preview my progress email</button>
          <button data-testid="send-book-progress-btn" onClick={sendBookProgress} disabled={bookSending} className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm">{bookSending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send me a preview</button>
        </div>
        {bookEmail && (
          <div className="mt-4 bg-cream2 rounded-xl p-4" data-testid="book-progress-preview">
            <p className="text-xs text-subtle">To: {bookEmail.to}</p>
            <p className="text-charcoal mt-2 text-sm"><b className="text-teal">{(bookEmail.stats?.words || 0).toLocaleString()}</b> words · <b className="text-teal">{bookEmail.stats?.chapters || 0}</b> chapters · <b className="text-teal">{bookEmail.stats?.memories || 0}</b> memories</p>
            <p className="text-xs mt-2">{bookEmail.live_sending ? <span className="text-sage font-semibold">Live sending is on.</span> : <span className="text-coral font-semibold">Preview only — add a Resend key to send live.</span>}</p>
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-line p-6">
        <h2 className="font-serif text-xl text-charcoal flex items-center gap-2 mb-2"><Shield size={18} className="text-sage" /> Your data & privacy</h2>
        <p className="text-subtle text-sm mb-4">Your story is private by default and is never shared or used as an example.</p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="export-data-btn" onClick={exportData} className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm"><Download size={16} /> Export my data</button>
          <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm"><LogOut size={16} /> Log out</button>
          <button data-testid="delete-account-btn" onClick={deleteAccount} className="inline-flex items-center gap-2 text-destructive/80 hover:text-destructive px-5 py-2.5 rounded-full font-medium text-sm"><Trash2 size={16} /> Delete account</button>
        </div>
        <div className="mt-5 pt-4 border-t border-line text-sm text-subtle">
          <Link to="/help" data-testid="settings-faq-link" className="text-teal hover:underline">FAQ</Link>
          <span className="mx-2">·</span>
          <Link to="/privacy" data-testid="settings-privacy-link" className="text-teal hover:underline">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" data-testid="settings-terms-link" className="text-teal hover:underline">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}
